if (!customElements.get('monoree-product-share')) {
  customElements.define(
    'monoree-product-share',
    class MonoreeProductShare extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;

        this.trigger = this.querySelector('[data-share-trigger]');
        this.details = this.querySelector('[data-share-details]');
        this.summary = this.querySelector('[data-share-summary]');
        this.urlInput = this.querySelector('[data-share-url]');
        this.copyButton = this.querySelector('[data-share-copy]');
        this.closeButton = this.querySelector('[data-share-close]');
        this.status = this.querySelector('[data-share-status]');
        if (!this.trigger || !this.details || !this.summary || !this.urlInput || !this.status) return;

        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.actionId = (this.actionId || 0) + 1;
        this.updateUrl(this.urlInput.value);
        this.setBusy(false);
        this.details.open = false;
        this.details.hidden = true;
        this.summary.hidden = true;
        this.trigger.hidden = false;
        if (this.copyButton) this.copyButton.hidden = false;
        if (this.closeButton) this.closeButton.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'false');

        this.trigger.addEventListener('click', (event) => this.share(event), options);
        this.copyButton?.addEventListener('click', (event) => this.copy(event), options);
        this.closeButton?.addEventListener('click', () => this.closePanel(true), options);
        this.urlInput.addEventListener('click', () => this.urlInput.select(), options);
        this.details.addEventListener('toggle', () => {
          this.trigger.setAttribute('aria-expanded', String(this.details.open));
          if (!this.details.open) this.details.hidden = true;
        }, options);
        document.addEventListener('pointerdown', (event) => {
          if (this.contains(event.target)) return;
          if (this.details.open) this.closePanel(false);
          else this.setStatus('');
        }, options);
        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.details.open) return;
          event.preventDefault();
          this.closePanel(true);
        }, options);
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        clearTimeout(this.statusTimer);
        this.actionId = (this.actionId || 0) + 1;
        if (this.trigger) this.setBusy(false);
      }

      sanitizeUrl(value) {
        let source;
        try {
          source = new URL(value || window.location.href, window.location.href);
          if (!['http:', 'https:'].includes(source.protocol)) throw new TypeError();
        } catch {
          source = new URL(window.location.href);
        }
        const result = new URL(window.location.origin);
        result.pathname = source.pathname;
        const variant = source.searchParams.get('variant');
        if (variant && /^\d+$/.test(variant)) result.searchParams.set('variant', variant);
        return result.href;
      }

      updateUrl(url) {
        this.urlToShare = this.sanitizeUrl(url);
        const input = this.urlInput || this.querySelector('[data-share-url]');
        if (input) input.value = this.urlToShare;
        this.setStatus('');
      }

      setStatus(message, temporary = false) {
        clearTimeout(this.statusTimer);
        const status = this.status || this.querySelector('[data-share-status]');
        if (status) status.textContent = message;
        if (message && temporary) this.statusTimer = setTimeout(() => this.setStatus(''), 4000);
      }

      setBusy(busy) {
        this.busy = busy;
        this.trigger.setAttribute('aria-busy', String(busy));
        this.copyButton?.setAttribute('aria-busy', String(busy));
      }

      isCurrentAction(actionId, url) {
        return Boolean(this.controller && this.actionId === actionId && this.dismissedActionId !== actionId && this.urlToShare === url);
      }

      async share(event) {
        event.preventDefault();
        if (this.busy) return;
        if (this.details.open) {
          this.closePanel(false);
          return;
        }
        const actionId = ++this.actionId;
        const url = this.urlToShare;
        this.setBusy(true);
        this.setStatus('');
        try {
          if (typeof navigator.share === 'function') {
            try {
              await navigator.share({ title: this.dataset.title || document.title, url });
            } catch (error) {
              if (error?.name !== 'AbortError' && this.isCurrentAction(actionId, url)) {
                await this.copyUrl(url, actionId);
              }
            }
          } else {
            await this.copyUrl(url, actionId);
          }
        } finally {
          if (this.actionId === actionId) this.setBusy(false);
        }
      }

      async copy(event) {
        event.preventDefault();
        if (this.busy) return;
        const actionId = ++this.actionId;
        const url = this.urlToShare;
        this.setBusy(true);
        this.setStatus('');
        try {
          await this.copyUrl(url, actionId);
        } finally {
          if (this.actionId === actionId) this.setBusy(false);
        }
      }

      async copyUrl(url, actionId) {
        try {
          if (typeof navigator.clipboard?.writeText !== 'function') throw new TypeError();
          await navigator.clipboard.writeText(url);
          if (!this.isCurrentAction(actionId, url)) return;
          const restoreFocus = this.details.open && this.details.contains(document.activeElement);
          this.closePanel(false);
          this.setStatus(this.dataset.success || '', true);
          if (restoreFocus) this.trigger.focus({ preventScroll: true });
        } catch {
          if (!this.isCurrentAction(actionId, url)) return;
          this.details.hidden = false;
          this.details.open = true;
          this.trigger.setAttribute('aria-expanded', 'true');
          this.setStatus(this.dataset.manual || '');
          this.urlInput.focus({ preventScroll: true });
          this.urlInput.select();
        }
      }

      closePanel(restoreFocus) {
        this.dismissedActionId = this.actionId;
        this.details.open = false;
        this.details.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.setStatus('');
        if (restoreFocus) this.trigger.focus({ preventScroll: true });
      }
    }
  );
}
